import * as React from 'react';
import {
  ArrowSquareOut,
  CaretDown,
  CaretRight,
  FolderOpen,
  GitPullRequest,
  ShieldCheck,
  Tag,
  Wrench,
} from '@phosphor-icons/react';
import { Checkbox, Table, type VirtualTableRow } from '@fragments-sdk/ui';
import {
  flexRender,
  type Row,
  type RowSelectionState,
  type Table as ReactTable,
} from '@tanstack/react-table';
import {
  buildGitHubUrl,
  classNames,
  severityBreakdown,
} from '../findings-display-model';
import { FindingsTableRow, thClass } from '../findings-table';
import type { FindingDoc } from '../findings-types';
import { FindingsPagination } from '../findings-navigation';
import type { FindingsGroupBy } from '../findings-url';
import type { StatusPatchSummary } from '../findings-operator-model';
import { GroupCategoryMix, GroupTopFiles } from './group-summary';
import type { EnrichedGroup } from './repository-groups';
import { ActionRail, RowActionButton } from '@/components/table-kit';
import s from '../findings.module.scss';

export type RepositoryVirtualItem =
  | {
      kind: 'group';
      group: EnrichedGroup;
      collapsed: boolean;
      band: 'default' | 'alt';
    }
  | { kind: 'row'; row: Row<FindingDoc>; band: 'default' | 'alt' };

type RepositoryTableProps = {
  table: ReactTable<FindingDoc>;
  groupBy: FindingsGroupBy;
  virtualRows: VirtualTableRow<RepositoryVirtualItem>[];
  paddingTop: number;
  paddingBottom: number;
  rowSelection: RowSelectionState;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  measureRow: (node: Element | null) => void;
  expandedGroups: Set<string>;
  groupChildrenLoading: boolean;
  repoFullName?: string;
  repoRef?: string;
  readOnly: boolean;
  fixLoading: boolean;
  fixPrCreated: boolean;
  canCreateWaiver: boolean;
  currentPage: number;
  totalPages: number;
  pageStart: number;
  pageEnd: number;
  totalItems: number;
  onToggleGroup: (key: string) => void;
  onAutofix: (ids: string[]) => void;
  onBulkStatus: (
    ids: string[],
    status: 'open' | 'ignored' | 'resolved',
  ) => Promise<StatusPatchSummary | null>;
  onPageChange: (page: number) => void;
};

export function RepositoryFindingsTable({
  table,
  groupBy,
  virtualRows,
  paddingTop,
  paddingBottom,
  rowSelection,
  setRowSelection,
  measureRow,
  expandedGroups,
  groupChildrenLoading,
  repoFullName,
  repoRef,
  readOnly,
  fixLoading,
  fixPrCreated,
  canCreateWaiver,
  currentPage,
  totalPages,
  pageStart,
  pageEnd,
  totalItems,
  onToggleGroup,
  onAutofix,
  onBulkStatus,
  onPageChange,
}: RepositoryTableProps) {
  return (
    <>
      <Table
        aria-label="Findings"
        className={classNames(
          s.findingsTable,
          groupBy === 'file' && s.findingsTableFileMode,
        )}
        wrapperClassName={s.findingsTableShell}
      >
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.HeaderCell
                  key={header.id}
                  className={thClass(header.column.id)}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          ))}
        </Table.Head>
        <Table.Body>
          {paddingTop > 0 && (
            <tr aria-hidden="true">
              <td colSpan={5} style={{ height: paddingTop, padding: 0 }} />
            </tr>
          )}
          {virtualRows.map((vr) => {
            const item = vr.item;
            if (!item) return null;
            if (item.kind === 'row') {
              return (
                <FindingsTableRow
                  key={`r:${item.row.id}`}
                  row={item.row}
                  selected={item.row.getIsSelected()}
                  rowRef={measureRow}
                  dataIndex={vr.index}
                  rowBand={item.band}
                />
              );
            }
            return (
              <RepositoryGroupRow
                key={`g:${item.group.key}`}
                group={item.group}
                collapsed={item.collapsed}
                band={item.band}
                groupBy={groupBy}
                dataIndex={vr.index}
                measureRow={measureRow}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                expandedGroups={expandedGroups}
                groupChildrenLoading={groupChildrenLoading}
                repoFullName={repoFullName}
                repoRef={repoRef}
                readOnly={readOnly}
                fixLoading={fixLoading}
                fixPrCreated={fixPrCreated}
                canCreateWaiver={canCreateWaiver}
                onToggleGroup={onToggleGroup}
                onAutofix={onAutofix}
                onBulkStatus={onBulkStatus}
              />
            );
          })}
          {paddingBottom > 0 && (
            <tr aria-hidden="true">
              <td
                colSpan={5}
                style={{ height: paddingBottom, padding: 0, border: 0 }}
              />
            </tr>
          )}
        </Table.Body>
      </Table>

      <FindingsPagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageStart={pageStart}
        pageEnd={pageEnd}
        totalItems={totalItems}
        itemLabel={groupBy === 'none' ? 'finding' : 'finding group'}
        onPageChange={onPageChange}
      />
    </>
  );
}

function RepositoryGroupRow({
  group,
  collapsed,
  band,
  groupBy,
  dataIndex,
  measureRow,
  rowSelection,
  setRowSelection,
  expandedGroups,
  groupChildrenLoading,
  repoFullName,
  repoRef,
  readOnly,
  fixLoading,
  fixPrCreated,
  canCreateWaiver,
  onToggleGroup,
  onAutofix,
  onBulkStatus,
}: {
  group: EnrichedGroup;
  collapsed: boolean;
  band: 'default' | 'alt';
  groupBy: FindingsGroupBy;
  dataIndex: number;
  measureRow: (node: Element | null) => void;
  rowSelection: RowSelectionState;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  expandedGroups: Set<string>;
  groupChildrenLoading: boolean;
  repoFullName?: string;
  repoRef?: string;
  readOnly: boolean;
  fixLoading: boolean;
  fixPrCreated: boolean;
  canCreateWaiver: boolean;
  onToggleGroup: (key: string) => void;
  onAutofix: (ids: string[]) => void;
  onBulkStatus: (
    ids: string[],
    status: 'open' | 'ignored' | 'resolved',
  ) => Promise<StatusPatchSummary | null>;
}) {
  const groupRepoUrl =
    groupBy === 'file' && group.key !== '(no file)'
      ? buildGitHubUrl(repoFullName, group.repoRef ?? repoRef, group.key)
      : null;
  const groupIsLoadingChildren =
    !collapsed && groupChildrenLoading && expandedGroups.has(group.key);
  const selectedCount = group.rows.reduce(
    (n, row) => n + (rowSelection[row.id] ? 1 : 0),
    0,
  );
  const allLoadedSelected =
    selectedCount === group.rows.length && group.rows.length > 0;
  const someSelected = selectedCount > 0 && !allLoadedSelected;

  return (
    <Table.Row
      ref={(node) => {
        measureRow(node);
      }}
      band={band}
      data-index={dataIndex}
      className={s.groupRow}
      data-collapsed={collapsed ? true : undefined}
    >
      <td colSpan={5}>
        <div className={s.groupRowInner}>
          <div className={s.groupSelectCell}>
            <Checkbox
              size="sm"
              className={s.checkboxControl}
              checked={allLoadedSelected}
              indeterminate={someSelected}
              disabled={readOnly || group.rows.length === 0}
              onCheckedChange={(checked) => {
                if (readOnly) return;
                setRowSelection((prev) => {
                  const next = { ...prev };
                  if (!checked) {
                    for (const row of group.rows) delete next[row.id];
                  } else {
                    for (const row of group.rows) next[row.id] = true;
                  }
                  return next;
                });
              }}
              aria-label={
                readOnly
                  ? 'Selection disabled in read-only demo'
                  : group.rows.length === 0
                    ? 'No loaded findings in this group'
                    : allLoadedSelected
                      ? 'Deselect group'
                      : group.rows.length < group.totalCount
                        ? `Select ${group.rows.length} loaded findings (${group.totalCount - group.rows.length} more not yet loaded)`
                        : 'Select group'
              }
            />
          </div>
          <div className={s.groupAttentionCell}>
            <button
              type="button"
              className={s.groupCaret}
              onClick={() => onToggleGroup(group.key)}
              aria-label={collapsed ? 'Expand group' : 'Collapse group'}
            >
              {collapsed ? <CaretRight size={11} /> : <CaretDown size={11} />}
            </button>
            <GroupIcon groupBy={groupBy} />
          </div>
          <div className={s.groupTitleCell}>
            <div className={s.groupTitleStack}>
              <span className={s.groupTitlePrimary}>
                <span className={s.groupTitle} title={group.key}>
                  {group.label}
                </span>
                {groupRepoUrl && (
                  <a
                    href={groupRepoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={s.groupRepoLink}
                  >
                    Open in repo
                    <ArrowSquareOut size={9} />
                  </a>
                )}
                <span className={s.groupMeta}>
                  {severityBreakdown(group.severityCounts)}
                </span>
                {group.fixableCount > 0 && (
                  <span className={s.groupFixable}>
                    <Wrench size={10} /> {group.fixableCount}{' '}
                    {groupBy === 'decision' ? 'safe fixes' : 'fixable'}
                  </span>
                )}
                {groupIsLoadingChildren ? (
                  <span className={s.groupMeta}>Loading findings...</span>
                ) : (
                  !collapsed &&
                  group.rows.length < group.totalCount && (
                    <span
                      className={s.groupMeta}
                      title={`${group.rows.length} of ${group.totalCount} findings loaded for this group`}
                    >
                      {group.rows.length} of {group.totalCount} loaded
                    </span>
                  )
                )}
              </span>
              {(groupBy === 'file' ||
                groupBy === 'rule' ||
                groupBy === 'decision') && (
                <span className={s.groupSubline}>
                  {(groupBy === 'rule' || groupBy === 'decision') && (
                    <GroupTopFiles files={group.topFiles} />
                  )}
                  <GroupCategoryMix items={group.categoryItems} />
                </span>
              )}
            </div>
          </div>
          {!readOnly && (
            <div className={s.groupActions}>
              <ActionRail>
                {group.fixableIds.length > 0 && !fixPrCreated && (
                  <RowActionButton
                    variant="primary"
                    disabled={fixLoading}
                    onClick={() => onAutofix(group.fixableIds)}
                  >
                    <Wrench size={11} />
                    Fix {group.fixableIds.length}
                  </RowActionButton>
                )}
                {group.openIds.length > 0 && (
                  <RowActionButton
                    disabled={!canCreateWaiver}
                    title={
                      canCreateWaiver
                        ? undefined
                        : 'Upgrade to Pro to ignore findings with cloud waivers.'
                    }
                    onClick={() => void onBulkStatus(group.openIds, 'ignored')}
                  >
                    Ignore {group.openIds.length}
                  </RowActionButton>
                )}
              </ActionRail>
            </div>
          )}
        </div>
      </td>
    </Table.Row>
  );
}

function GroupIcon({ groupBy }: { groupBy: FindingsGroupBy }) {
  if (groupBy === 'file')
    return <FolderOpen size={13} className={s.groupIcon} />;
  if (groupBy === 'decision') {
    return <ShieldCheck size={13} weight="bold" className={s.groupIcon} />;
  }
  if (groupBy === 'pr') {
    return <GitPullRequest size={13} weight="bold" className={s.groupIcon} />;
  }
  return <Tag size={13} className={s.groupIcon} />;
}
